import React, { useState } from 'react';
import { Html, Text } from '@react-three/drei';
import { useMediaXRSession } from '../hooks/useMediaXRSession';
import { ScreenPropItem, useWorldStore } from '../stores/worldStore';
import { openMediaPanel } from './MediaPanelDialogs';
import './MediaPanels.css';
import { stepPanel, PanelStep } from '../utils/mediaPanelPlacement';

function XRAction({ label, x, y = 0, onClick }: { label: string; x: number; y?: number; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return <group position={[x, y, 0]}>
    <mesh onPointerOver={() => setHover(true)} onPointerOut={() => setHover(false)} onClick={e => { e.stopPropagation(); onClick(); }}>
      <boxGeometry args={[1.2, .26, .04]} /><meshBasicMaterial color={hover ? '#13675f' : '#233446'} />
    </mesh>
    <Text position={[0, 0, .025]} fontSize={.085} maxWidth={1.15} textAlign="center" raycast={() => null}>{label}</Text>
  </group>;
}

export default function MediaPanelActions({ id, item, editing = false, canConfigure, onEdit, onDone, onChange }: {
  id: string; item: ScreenPropItem; editing?: boolean; onEdit?: () => void; onDone?: () => void;
  canConfigure?: boolean;
  onChange?: (updates: Partial<ScreenPropItem>) => void;
}) {
  const session = useMediaXRSession();
  const canBuild = useWorldStore(s => s.canBuild);
  const [error, setError] = useState('');
  const usePanel = async (action: 'share' | 'url') => {
    try { if (session) await session.end(); openMediaPanel(id, action); }
    catch { setError('Could not leave VR. Exit VR to use the panel controls.'); }
  };
  const actions: { label: string; run: () => void }[] = [];
  if (item.type === 'browser') {
    if (canConfigure ?? canBuild) actions.push({ label: session ? 'Exit VR: set URL' : 'Change URL', run: () => void usePanel('url') });
    actions.push({ label: session ? 'Exit VR: share tab' : 'Share a tab', run: () => void usePanel('share') });
  } else if (item.type === 'screen-share') actions.push({ label: session ? 'Exit VR: share' : 'Sharing Controls', run: () => void usePanel('share') });
  if (onEdit) actions.push({ label: editing ? 'Done Placing' : 'Edit Placement', run: editing ? onDone! : onEdit });
  const height = item.type === 'browser' ? 3 : 2.25;
  if (session) return <group position={[0, -height / 2 - .3, .12]}>
    {actions.map((a, i) => <XRAction key={a.label} label={a.label} x={(i - (actions.length - 1) / 2) * 1.3} onClick={a.run} />)}
    {editing && onChange && <>
      {['Left', 'Right', 'Up', 'Down', 'Near', 'Far'].map((label, i) => <XRAction key={label} label={label} x={(i % 3 - 1) * 1.3} y={-.35 * (1 + Math.floor(i / 3))} onClick={() => {
        const position = stepPanel(item.position, item.rotation[1], label as PanelStep);
        onChange({ position });
      }} />)}
      <XRAction label="Rotate 15°" x={-1.3} y={-1.05} onClick={() => onChange({ rotation: [item.rotation[0], item.rotation[1] + Math.PI / 12, item.rotation[2]] })} />
      <XRAction label="Smaller" x={0} y={-1.05} onClick={() => onChange({ scale: Math.max(.2, item.scale - .1) })} />
      <XRAction label="Larger" x={1.3} y={-1.05} onClick={() => onChange({ scale: Math.min(10, item.scale + .1) })} />
    </>}
    {error && <Text position={[0, -.4, 0]} fontSize={.09} maxWidth={4}>{error}</Text>}
  </group>;
  return <Html center position={[0, -height / 2 - .25, .1]} zIndexRange={[30, 0]}>
    <div className="media-panel-actions" onPointerDown={e => e.stopPropagation()}>
      {actions.map(a => <button key={a.label} onClick={e => { e.stopPropagation(); a.run(); }}>{a.label}</button>)}
      {error && <span role="alert">{error}</span>}
    </div>
  </Html>;
}
